import axios from 'axios';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { resolve } from '@/api/endPoints';
import { UPLOAD_TYPE_POST_MEDIA } from '@/config/constants';
import axiosClient from '@/utils/axios';
import { getMediaMetadata } from './mediaMetadata';

export interface AdManagerCountryOption {
  code: string;
  name: string;
}

export interface AdManagerTargetRawData {
  id?: string;
  key?: string;
  name?: string;
  type?: string;
  path?: string[];
  description?: string;
  topic?: string;
  disambiguation_category?: string;
  audience_size?: number;
  audience_size_lower_bound?: number;
  audience_size_upper_bound?: number;
  country_code?: string;
  country_name?: string;
  region?: string;
  region_id?: string;
  latitude?: number;
  longitude?: number;
}

export interface AdManagerTargetOption {
  id: string;
  name: string;
  raw_data?: AdManagerTargetRawData;
}

export interface AdManagerNamedOption {
  id: string;
  name: string;
}

export interface AdManagerTargetLocation extends AdManagerTargetOption {
  coordinate?: {
    lat: number;
    lng: number;
  };
  radius_km?: number;
}

export interface AdManagerMediaFile {
  id: string;
  image_uuid: string;
  name: string;
  kind: 'image' | 'video' | 'file';
  preview_url: string;
  original_filesize_bytes: number;
}

export type AdCampaignDraftStatus =
  | 'draft'
  | 'publishing'
  | 'published'
  | 'failed';

export interface AdCampaignDraft {
  uuid?: string;
  created?: string;
  modified?: string;
  location_uuid?: string;
  meta_ad_account_uuid?: string;
  name: string;
  objective?: string;
  placements?: string[];
  upload_to_platform_status?: number;
  upload_to_platform_status_key?: AdCampaignDraftStatus;
  upload_to_platform_status_label?: string;
  meta_campaign_id?: string;
  meta_adset_id?: string;
  meta_ad_id?: string;
  meta_creative_id?: string;
  is_disabled?: boolean;
  published_at?: string | null;
  failure_message?: string;
  platform_response_data?: Record<string, any>;
  platform_error_data?: Record<string, any>;
  radius_km?: number;
  target_locations?: AdManagerTargetLocation[];
  target_languages?: AdManagerTargetOption[];
  target_interests?: AdManagerTargetOption[];
  target_behaviors?: AdManagerTargetOption[];
  target_custom_audiences?: AdManagerTargetOption[];
  pixel?: AdManagerNamedOption | Record<string, never>;
  target_event?: AdManagerNamedOption | Record<string, never>;
  special_ad_categories?: string[];
  special_ad_category_country?: string[];
  age_min?: number;
  age_max?: number;
  gender?: string;
  dsa_beneficiary?: string;
  dsa_payor?: string;
  primary_text?: string;
  headline?: string;
  landing_url?: string;
  cta?: string;
  budget_type?: string;
  budget_amount_cents?: number | null;
  currency?: string;
  start_date?: string | null;
  end_date?: string | null;
  bid_strategy?: string;
  max_bid_amount_cents?: number | null;
  media_files?: AdManagerMediaFile[];
  thumbnail_url?: string;
}

interface AdManagerState {
  drafts: AdCampaignDraft[];
  activeDraftUuid: string | null;
  draftsCount: number;
  draftsNextUrl: string | null;
  draftsPreviousUrl: string | null;
  countryOptions: AdManagerCountryOption[];
  pixelOptions: AdManagerNamedOption[];
  loadingDrafts: boolean;
  loadingCountries: boolean;
  loadingPixels: boolean;
  pixelsLoaded: boolean;
  savingDraft: boolean;
  publishingDraftUuid: string | null;
  copyingDraftUuid: string | null;
  deletingDraftUuid: string | null;
  togglingDisabledDraftUuid: string | null;
  uploadingMedia: boolean;
  error: string | null;
  fieldErrors: Record<string, any>;
}

const initialState: AdManagerState = {
  drafts: [],
  activeDraftUuid: null,
  draftsCount: 0,
  draftsNextUrl: null,
  draftsPreviousUrl: null,
  countryOptions: [],
  pixelOptions: [],
  loadingDrafts: false,
  loadingCountries: false,
  loadingPixels: false,
  pixelsLoaded: false,
  savingDraft: false,
  publishingDraftUuid: null,
  copyingDraftUuid: null,
  deletingDraftUuid: null,
  togglingDisabledDraftUuid: null,
  uploadingMedia: false,
  error: null,
  fieldErrors: {}
};

const getErrorMessage = (error: any, fallback: string) =>
  typeof error?.response?.data?.detail === 'string'
    ? error.response.data.detail
    : fallback;

const hasFieldErrors = (fieldErrors: Record<string, any>) =>
  Object.entries(fieldErrors).some(
    ([field, messages]) =>
      field !== 'detail' &&
      messages !== undefined &&
      messages !== null &&
      !(Array.isArray(messages) && messages.length === 0)
  );

const adManagerSlice = createSlice({
  name: 'adManager',
  initialState,
  reducers: {
    setActiveDraftUuid: (state, action: PayloadAction<string | null>) => {
      state.activeDraftUuid = action.payload;
    },
    setDrafts: (state, action: PayloadAction<AdCampaignDraft[]>) => {
      state.drafts = action.payload;
      state.draftsCount = action.payload.length;
      state.draftsNextUrl = null;
      state.draftsPreviousUrl = null;
      if (
        state.activeDraftUuid &&
        !action.payload.some((draft) => draft.uuid === state.activeDraftUuid)
      ) {
        state.activeDraftUuid = null;
      }
    },
    setDraftsPage: (
      state,
      action: PayloadAction<{
        count: number;
        drafts: AdCampaignDraft[];
        next: string | null;
        previous: string | null;
      }>
    ) => {
      state.drafts = action.payload.drafts;
      state.draftsCount = action.payload.count;
      state.draftsNextUrl = action.payload.next;
      state.draftsPreviousUrl = action.payload.previous;
      if (
        state.activeDraftUuid &&
        !action.payload.drafts.some(
          (draft) => draft.uuid === state.activeDraftUuid
        )
      ) {
        state.activeDraftUuid = null;
      }
    },
    upsertDraft: (state, action: PayloadAction<AdCampaignDraft>) => {
      const index = state.drafts.findIndex(
        (draft) => draft.uuid === action.payload.uuid
      );
      if (index >= 0) {
        state.drafts[index] = action.payload;
      } else {
        state.drafts = [action.payload, ...state.drafts];
        state.draftsCount += 1;
      }
      state.activeDraftUuid = action.payload.uuid || null;
    },
    removeDraft: (state, action: PayloadAction<string>) => {
      state.drafts = state.drafts.filter(
        (draft) => draft.uuid !== action.payload
      );
      state.draftsCount = Math.max(0, state.draftsCount - 1);
      if (state.activeDraftUuid === action.payload) {
        state.activeDraftUuid = null;
      }
    },
    setCountryOptions: (
      state,
      action: PayloadAction<AdManagerCountryOption[]>
    ) => {
      state.countryOptions = action.payload;
    },
    setPixelOptions: (state, action: PayloadAction<AdManagerNamedOption[]>) => {
      state.pixelOptions = action.payload;
      state.pixelsLoaded = true;
    },
    resetPixelOptions: (state) => {
      state.pixelOptions = [];
      state.pixelsLoaded = false;
    },
    setLoadingDrafts: (state, action: PayloadAction<boolean>) => {
      state.loadingDrafts = action.payload;
    },
    setLoadingCountries: (state, action: PayloadAction<boolean>) => {
      state.loadingCountries = action.payload;
    },
    setLoadingPixels: (state, action: PayloadAction<boolean>) => {
      state.loadingPixels = action.payload;
    },
    setSavingDraft: (state, action: PayloadAction<boolean>) => {
      state.savingDraft = action.payload;
    },
    setPublishingDraftUuid: (state, action: PayloadAction<string | null>) => {
      state.publishingDraftUuid = action.payload;
    },
    setCopyingDraftUuid: (state, action: PayloadAction<string | null>) => {
      state.copyingDraftUuid = action.payload;
    },
    setDeletingDraftUuid: (state, action: PayloadAction<string | null>) => {
      state.deletingDraftUuid = action.payload;
    },
    setTogglingDisabledDraftUuid: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.togglingDisabledDraftUuid = action.payload;
    },
    setUploadingMedia: (state, action: PayloadAction<boolean>) => {
      state.uploadingMedia = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setFieldErrors: (state, action: PayloadAction<Record<string, any>>) => {
      state.fieldErrors = action.payload;
    },
    clearAdManagerErrors: (state) => {
      state.error = null;
      state.fieldErrors = {};
    }
  }
});

export const { setActiveDraftUuid, clearAdManagerErrors } =
  adManagerSlice.actions;

export default adManagerSlice.reducer;

export const fetchAdManagerCountries = () => async (dispatch: any) => {
  dispatch(adManagerSlice.actions.setLoadingCountries(true));
  dispatch(adManagerSlice.actions.setError(null));

  try {
    const url = resolve('ad_campaign_country_options', {}, {});
    const response = await axiosClient.get(url);
    dispatch(adManagerSlice.actions.setCountryOptions(response.data || []));
  } catch (error: any) {
    dispatch(
      adManagerSlice.actions.setError(
        getErrorMessage(error, 'Failed to fetch country options.')
      )
    );
  } finally {
    dispatch(adManagerSlice.actions.setLoadingCountries(false));
  }
};

export const fetchAdManagerPixels =
  ({
    locationUuid,
    onError
  }: {
    locationUuid: string;
    onError?: (error: any) => void;
  }) =>
  async (dispatch: any) => {
    dispatch(adManagerSlice.actions.setLoadingPixels(true));
    dispatch(adManagerSlice.actions.setError(null));
    dispatch(adManagerSlice.actions.resetPixelOptions());

    try {
      const url = resolve(
        'ad_campaign_pixels',
        {},
        { location_uuid: locationUuid }
      );
      const response = await axiosClient.get(url);
      dispatch(adManagerSlice.actions.setPixelOptions(response.data || []));
    } catch (error: any) {
      dispatch(
        adManagerSlice.actions.setError(
          getErrorMessage(error, 'Failed to fetch Meta pixels.')
        )
      );
      onError?.(error);
    } finally {
      dispatch(adManagerSlice.actions.setLoadingPixels(false));
    }
  };

export const fetchAdCampaignDrafts =
  ({
    locationUuid,
    pageUrl,
    status,
    onError
  }: {
    locationUuid: string;
    pageUrl?: string | null;
    status?: string | null;
    onError?: (error: any) => void;
  }) =>
  async (dispatch: any) => {
    dispatch(adManagerSlice.actions.setLoadingDrafts(true));
    dispatch(adManagerSlice.actions.setError(null));

    try {
      const query: Record<string, string> = {
        location__uuid: locationUuid
      };
      if (status) {
        query.status = status;
      }
      const url = pageUrl || resolve('ad_campaign_drafts', {}, query);
      const response = await axiosClient.get(url);
      const data = response?.data;
      const drafts = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
        ? data
        : [];

      dispatch(
        adManagerSlice.actions.setDraftsPage({
          count:
            typeof data?.count === 'number'
              ? data.count
              : Array.isArray(data)
              ? data.length
              : drafts.length,
          drafts,
          next: data?.next ?? null,
          previous: data?.previous ?? null
        })
      );
    } catch (error: any) {
      dispatch(
        adManagerSlice.actions.setError(
          getErrorMessage(error, 'Failed to fetch campaign drafts.')
        )
      );
      onError?.(error);
    } finally {
      dispatch(adManagerSlice.actions.setLoadingDrafts(false));
    }
  };

export const fetchAdCampaignDraft =
  ({
    uuid,
    onError,
    onSuccess
  }: {
    uuid: string;
    onError?: (error: any) => void;
    onSuccess?: (draft: AdCampaignDraft) => void;
  }) =>
  async (dispatch: any) => {
    dispatch(adManagerSlice.actions.setError(null));

    try {
      const url = resolve('ad_campaign_draft_detail', { uuid }, {});
      const response = await axiosClient.get(url);
      dispatch(adManagerSlice.actions.upsertDraft(response.data));
      onSuccess?.(response.data);
      return response.data;
    } catch (error: any) {
      dispatch(
        adManagerSlice.actions.setError(
          getErrorMessage(error, 'Failed to fetch campaign draft.')
        )
      );
      onError?.(error);
      throw error;
    }
  };

export const saveAdCampaignDraft =
  ({
    uuid,
    payload,
    onSuccess,
    onError
  }: {
    uuid?: string;
    payload: Partial<AdCampaignDraft> & { location_uuid?: string };
    onSuccess?: (draft: AdCampaignDraft) => void;
    onError?: (error: any) => void;
  }) =>
  async (dispatch: any) => {
    dispatch(adManagerSlice.actions.setSavingDraft(true));
    dispatch(adManagerSlice.actions.setError(null));
    dispatch(adManagerSlice.actions.setFieldErrors({}));

    try {
      const url = uuid
        ? resolve('ad_campaign_draft_detail', { uuid }, {})
        : resolve('ad_campaign_drafts', {}, {});
      const response = uuid
        ? await axiosClient.patch(url, payload)
        : await axiosClient.post(url, payload);
      dispatch(adManagerSlice.actions.upsertDraft(response.data));
      onSuccess?.(response.data);
      return response.data;
    } catch (error: any) {
      const fieldErrors = error?.response?.data || {};
      dispatch(adManagerSlice.actions.setFieldErrors(fieldErrors));
      dispatch(
        adManagerSlice.actions.setError(
          hasFieldErrors(fieldErrors)
            ? null
            : getErrorMessage(error, 'Failed to save campaign draft.')
        )
      );
      onError?.(error);
      throw error;
    } finally {
      dispatch(adManagerSlice.actions.setSavingDraft(false));
    }
  };

export const publishAdCampaignDraft =
  ({
    uuid,
    onSuccess,
    onError
  }: {
    uuid: string;
    onSuccess?: (draft: AdCampaignDraft) => void;
    onError?: (error: any) => void;
  }) =>
  async (dispatch: any) => {
    dispatch(adManagerSlice.actions.setPublishingDraftUuid(uuid));
    dispatch(adManagerSlice.actions.setError(null));
    dispatch(adManagerSlice.actions.setFieldErrors({}));

    try {
      const url = resolve('ad_campaign_draft_publish', { uuid }, {});
      const response = await axiosClient.post(url);
      dispatch(adManagerSlice.actions.upsertDraft(response.data));
      onSuccess?.(response.data);
      return response.data;
    } catch (error: any) {
      const responseData = error?.response?.data || {};
      const failedCampaign = responseData?.campaign;
      if (failedCampaign?.uuid) {
        dispatch(adManagerSlice.actions.upsertDraft(failedCampaign));
      }
      if (error?.response?.status === 400) {
        dispatch(adManagerSlice.actions.setFieldErrors(responseData));
      }
      dispatch(
        adManagerSlice.actions.setError(
          error?.response?.status === 400 && hasFieldErrors(responseData)
            ? null
            : getErrorMessage(error, 'Failed to publish campaign.')
        )
      );
      onError?.(error);
      throw error;
    } finally {
      dispatch(adManagerSlice.actions.setPublishingDraftUuid(null));
    }
  };

export const copyAdCampaignDraft =
  ({
    uuid,
    copySuffix,
    onSuccess,
    onError
  }: {
    uuid: string;
    copySuffix?: string;
    onSuccess?: (draft: AdCampaignDraft) => void;
    onError?: (error: any) => void;
  }) =>
  async (dispatch: any) => {
    dispatch(adManagerSlice.actions.setCopyingDraftUuid(uuid));
    dispatch(adManagerSlice.actions.setError(null));
    dispatch(adManagerSlice.actions.setFieldErrors({}));

    try {
      const url = resolve('ad_campaign_draft_copy', { uuid }, {});
      const response = await axiosClient.post(
        url,
        copySuffix ? { copy_suffix: copySuffix } : {}
      );
      dispatch(adManagerSlice.actions.upsertDraft(response.data));
      onSuccess?.(response.data);
      return response.data;
    } catch (error: any) {
      dispatch(
        adManagerSlice.actions.setError(
          getErrorMessage(error, 'Failed to copy campaign draft.')
        )
      );
      onError?.(error);
      throw error;
    } finally {
      dispatch(adManagerSlice.actions.setCopyingDraftUuid(null));
    }
  };

export const setAdCampaignDraftDisabled =
  ({
    uuid,
    disabled,
    onSuccess,
    onError
  }: {
    uuid: string;
    disabled: boolean;
    onSuccess?: (draft: AdCampaignDraft) => void;
    onError?: (error: any) => void;
  }) =>
  async (dispatch: any) => {
    dispatch(adManagerSlice.actions.setTogglingDisabledDraftUuid(uuid));
    dispatch(adManagerSlice.actions.setError(null));

    try {
      const url = resolve('ad_campaign_draft_set_status', { uuid }, {});
      const response = await axiosClient.post(url, { is_disabled: disabled });
      dispatch(adManagerSlice.actions.upsertDraft(response.data));
      onSuccess?.(response.data);
      return response.data;
    } catch (error: any) {
      dispatch(
        adManagerSlice.actions.setError(
          getErrorMessage(
            error,
            disabled
              ? 'Failed to disable campaign.'
              : 'Failed to enable campaign.'
          )
        )
      );
      onError?.(error);
      throw error;
    } finally {
      dispatch(adManagerSlice.actions.setTogglingDisabledDraftUuid(null));
    }
  };

export const deleteAdCampaignDraft =
  ({
    uuid,
    onSuccess,
    onError
  }: {
    uuid: string;
    onSuccess?: () => void;
    onError?: (error: any) => void;
  }) =>
  async (dispatch: any) => {
    dispatch(adManagerSlice.actions.setDeletingDraftUuid(uuid));
    dispatch(adManagerSlice.actions.setError(null));

    try {
      const url = resolve('ad_campaign_draft_detail', { uuid }, {});
      await axiosClient.delete(url);
      dispatch(adManagerSlice.actions.removeDraft(uuid));
      onSuccess?.();
    } catch (error: any) {
      dispatch(
        adManagerSlice.actions.setError(
          getErrorMessage(error, 'Failed to delete campaign draft.')
        )
      );
      onError?.(error);
    } finally {
      dispatch(adManagerSlice.actions.setDeletingDraftUuid(null));
    }
  };

export const uploadAdManagerMediaFile =
  ({
    contentType,
    file,
    locationUuid,
    onProgress,
    onError,
    signal
  }: {
    contentType?: string;
    file: File;
    locationUuid: string;
    onProgress?: (percent: number) => void;
    onError?: (error: any) => void;
    signal?: AbortSignal;
  }) =>
  async (dispatch: any) => {
    dispatch(adManagerSlice.actions.setUploadingMedia(true));
    dispatch(adManagerSlice.actions.setError(null));

    try {
      const uploadUrl = resolve('create_post_media', {}, {});
      const uploadContentType = contentType || file.type;
      const mediaMetadata = await getMediaMetadata(file, uploadContentType);
      const { data: uploadPreset } = await axiosClient.post(
        uploadUrl,
        {
          content_type: uploadContentType,
          filesize_in_bytes: file.size,
          filename: file.name,
          location_uuid: locationUuid,
          ...mediaMetadata,
          upload_type: UPLOAD_TYPE_POST_MEDIA
        },
        { signal }
      );

      await axios.put(uploadPreset.url, file, {
        headers: {
          ...(uploadPreset.headers || {}),
          'Content-Type': uploadContentType
        },
        signal,
        onUploadProgress: (event) => {
          const total = event.total || file.size;
          if (!total) return;

          const percent = Math.max(
            0,
            Math.min(99, Math.round((event.loaded / total) * 100))
          );
          onProgress?.(percent);
        }
      });
      await axiosClient.put(
        uploadPreset.confirm_url,
        { success: true },
        { signal }
      );

      return uploadPreset;
    } catch (error: any) {
      if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
        throw error;
      }

      dispatch(
        adManagerSlice.actions.setError(
          getErrorMessage(error, 'Failed to upload media.')
        )
      );
      onError?.(error);
      throw error;
    } finally {
      dispatch(adManagerSlice.actions.setUploadingMedia(false));
    }
  };
